import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { 
  MapPin, Truck, User, Calendar, Package, ChevronRight, ChevronLeft,
  CheckCircle, Loader2, Route, Weight, Clock, AlertCircle, Search, 
  Users, Database, Trash2, Plus, Copy, FileText, Info, Link2, ExternalLink, RefreshCw, Phone
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import AddressInput from './AddressInput';
import VehicleSelector from './VehicleSelector';
import TransferDatePicker from './TransferDatePicker';
import OperationSelector from './OperationSelector';
import RoutePreviewMap from './RoutePreviewMap';
import MultiStopInput from './MultiStopInput';
import { vehicleTypes, operationTypes, merchandiseTypes, containerTypes } from '../mockData';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Étapes du wizard enrichi
const STEPS = [
  { id: 'trajet', label: 'Trajet', icon: Route },
  { id: 'details', label: 'Détails', icon: Package },
  { id: 'chauffeurs', label: 'Chauffeurs', icon: User },
  { id: 'vehicules', label: 'Véhicules', icon: Truck },
  { id: 'planning', label: 'Planification', icon: Calendar },
  { id: 'confirm', label: 'Confirmation', icon: CheckCircle }
];

const CreateTransferWizard = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  contractId, 
  contractNumber,
  clientName,
  clientId,
  terminology = 'Transfert',
  parentOperations = [],
  // Mode édition/duplication
  mode = 'create', // 'create', 'edit', 'duplicate'
  existingTransfer = null
}) => {
  // Déterminer le genre de la terminologie
  const isMasculine = terminology?.toLowerCase() === 'transfert';
  const { user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingResources, setLoadingResources] = useState(false);
  const [showContainerOptions, setShowContainerOptions] = useState(false);
  const [calculatedDistance, setCalculatedDistance] = useState(null);
  const [routeGeometry, setRouteGeometry] = useState([]);
  const [routeMeta, setRouteMeta] = useState(null); // { distance_km, duration_s, segments, is_fallback }
  const [routeLoading, setRouteLoading] = useState(false);
  
  // Ref pour éviter de réinitialiser le formulaire si déjà ouvert
  const wasOpenRef = useRef(false);
  const formInitializedRef = useRef(false);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Save as Template (PM Spec 05/05/2026)
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);
  const [pendingTemplateSnapshot, setPendingTemplateSnapshot] = useState(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Ressources disponibles
  const [localDrivers, setLocalDrivers] = useState([]);
  const [localVehicles, setLocalVehicles] = useState([]);
  const [externalDrivers, setExternalDrivers] = useState([]);
  const [externalVehicles, setExternalVehicles] = useState([]);
  const [loadingExternal, setLoadingExternal] = useState(false);

  // Filtres
  const [driverSearch, setDriverSearch] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [driverSource, setDriverSource] = useState('all');
  const [vehicleSource, setVehicleSource] = useState('all');

  // Form data enrichi
  const [formData, setFormData] = useState({
    // Opération mère
    parentOperationMode: 'existing',
    parentOperationId: '',
    // Template
    templateId: '',
    // Trajet
    departureAddress: '',
    departureCoordinates: null,
    arrivalAddress: '',
    arrivalCoordinates: null,
    departureAddressComplement: '',
    arrivalAddressComplement: '',
    intermediateStops: [],  // Multi-stop : [{address, coordinates, addressComplement}]
    // Détails
    vehicleType: '',
    vehicleTypeOther: '',
    operationType: '',
    operationTypeOther: '',
    merchandiseType: '',
    merchandiseTypeOther: '',
    containerType: '',
    weight: 1,
    numberOfTrips: 1,
    tripsNetWeights: [null],
    // Aller-retour (PM Spec 05/05/2026)
    tripType: 'one_way',  // "one_way" | "round_trip",
    returnCargoMode: null,  // "empty" | "loaded",
    returnMerchandiseDescription: '',
    returnWeight: '',
    returnDepartureDate: '',
    distance_km: null,
    // Nombre de camions PAR TRANSFERT (hérité de l'opération parente)
    numberOfTrucks: 1,
    maxTrucksPerTransfer: 10, // Max camions par transfert (limité par opération parente),
    maxTransfersToCreate: 50, // Max dates/transferts qu'on peut planifier en une fois
    // Planning - Mode calendrier interactif
    date: new Date().toISOString().split('T')[0],
    // Nouveau format: Array de { date: "YYYY-MM-DD", shift: "day"|"night" }
    plannedTransfers: [],
    // Legacy - gardé pour compatibilité mode édition
    transferDates: [],
    shift: 'day',
    plannedStartTime: '06:00',
    plannedEndTime: '18:00',
    notes: '',
    // Filtres opérations
    operationFilter: 'all',
    // Mode planificateur avancé
    usePlannerMode: true
  });

  // Assignations (multi-camions)
  const [assignments, setAssignments] = useState([]);
  
  // Opérations disponibles (toutes)
  const [availableOperations, setAvailableOperations] = useState([]);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [operationSearch, setOperationSearch] = useState('');
  
  // Opérations liées au contrat (détection automatique)
  const [linkedOperations, setLinkedOperations] = useState([]);

  // Fetch toutes les opérations disponibles
  const fetchAvailableOperations = useCallback(async () => {
    setLoadingOperations(true);
    try {
      const response = await axios.get(`${API}/transport-requests`, {
        headers: { 'Authorization': 'Bearer cookie-auth' },
        params: { limit: 500 }  // Removed status=all which causes 0 results
      });
      const operations = response.data?.requests || response.data || [];
      setAvailableOperations(operations);
      
      // Filtrer les opérations liées à ce contrat
      if (contractId) {
        const linked = operations.filter(op => 
          op.linkedContractId === contractId || 
          op.linkedContractNumber === contractNumber
        );
        setLinkedOperations(linked);
        
        // Pré-sélectionner uniquement si : une seule opération liée, aucune déjà sélectionnée,
        // ET l'utilisateur n'a pas explicitement choisi le mode "nouvelle opération"
        setFormData(prev => {
          if (linked.length === 1 && !prev.parentOperationId && prev.parentOperationMode !== 'new') {
            return {
              ...prev,
              parentOperationMode: 'existing',
              parentOperationId: linked[0].id
            };
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error fetching operations:', error);
    } finally {
      setLoadingOperations(false);
    }
  }, [contractId, contractNumber]);

  // Initialiser les dates individuelles quand numberOfTrucks change
  useEffect(() => {
    const count = formData.numberOfTrucks || 1;
    if (count > 1) {
      const baseDate = formData.date || new Date().toISOString().split('T')[0];
      const existingDates = formData.transferDates || [];
      const newDates = Array(count).fill(null).map((_, i) => {
        // Si une date existe déjà pour cet index, la garder
        if (existingDates[i]) return existingDates[i];
        // Sinon, incrémenter la date de base
        const d = new Date(baseDate);
        d.setDate(d.getDate() + i);
        return d.toISOString().split('T')[0];
      });
      setFormData(prev => ({ ...prev, transferDates: newDates }));
    }
  }, [formData.numberOfTrucks, formData.date]);

  // Fetch templates (type=transfer prioritaire, fallback type=operation pour compat)
  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      // Récupère les templates "transfer" + ceux d'operation (catalog combiné, badge distinguera)
      const [transferRes, opRes] = await Promise.all([
        axios.get(`${API}/templates?type=transfer`).catch(() => ({ data: [] })),
        axios.get(`${API}/templates?type=operation`).catch(() => ({ data: [] })),
      ]);
      const merged = [
        ...(transferRes.data || []).map(t => ({ ...t, _isTransferTemplate: true })),
        ...(opRes.data || []).map(t => ({ ...t, _isTransferTemplate: false })),
      ];
      setTemplates(merged);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  // Apply template
  const applyTemplate = (template) => {
    if (!template) return;

    setSelectedTemplate(template);
    setFormData(prev => ({
      ...prev,
      templateId: template.id,
      departureAddress: template.departureAddress || prev.departureAddress,
      departureCoordinates: template.departureCoordinates || prev.departureCoordinates,
      arrivalAddress: template.arrivalAddress || prev.arrivalAddress,
      arrivalCoordinates: template.arrivalCoordinates || prev.arrivalCoordinates,
      vehicleType: template.vehicleType || prev.vehicleType,
      operationType: template.operationType || prev.operationType,
      merchandiseType: template.merchandiseType || prev.merchandiseType,
      containerType: template.containerType || prev.containerType,
      weight: template.weight || prev.weight,
      numberOfTrucks: template.numberOfTrucks || prev.numberOfTrucks,
      shift: template.shift || prev.shift,
      plannedStartTime: template.plannedStartTime || prev.plannedStartTime,
      plannedEndTime: template.plannedEndTime || prev.plannedEndTime,
      notes: template.notes || prev.notes,
      // Aller-retour & multi-stop (PM Spec 05/05/2026)
      tripType: template.tripType || prev.tripType,
      returnCargoMode: template.returnCargoMode || prev.returnCargoMode,
      returnMerchandiseDescription: template.returnMerchandiseDescription || prev.returnMerchandiseDescription,
      returnWeight: template.returnWeight ?? prev.returnWeight,
      intermediateStops: template.stops || prev.intermediateStops,
      distance_km: template.distance_km ?? prev.distance_km,
    }));
    
    // Update container options visibility
    const selectedOp = operationTypes.find(op => op.value === template.operationType);
    setShowContainerOptions(selectedOp?.showContainerOption || false);
    
    toast.success(`Template "${template.name}" appliqué`);
  };

  // Initialisation - ne réinitialise QUE lors de l'ouverture (transition false → true)
  useEffect(() => {
    // Détecte une vraie ouverture (passage de fermé à ouvert)
    const justOpened = isOpen && !wasOpenRef.current;
    
    if (justOpened) {
      // Réinitialiser uniquement à la première ouverture
      formInitializedRef.current = false;
      resetForm();
      fetchTemplates();
    }
    
    // Mettre à jour la référence
    wasOpenRef.current = isOpen;
    
    // Si le modal se ferme, permettre une nouvelle initialisation à la prochaine ouverture
    if (!isOpen) {
      formInitializedRef.current = false;
    }
  }, [isOpen, fetchTemplates]);

  const resetForm = () => {
    setCurrentStep(0);
    setDriverSearch('');
    setVehicleSearch('');
    setCalculatedDistance(null);
    setSelectedTemplate(null);
    
    // Mode duplication ou édition
    if ((mode === 'duplicate' || mode === 'edit') && existingTransfer) {
      const stops = existingTransfer.stops || [];
      const departure = stops[0] || {};
      const arrival = stops[stops.length - 1] || {};
      // Stops intermédiaires = milieu de la liste (exclut départ/arrivée)
      const intermediateStops = stops.length > 2
        ? stops.slice(1, -1).map((s, i) => ({
            order: i + 1,
            address: s.address || '',
            city: s.city || '',
            country: s.country || 'CI',
            coordinates: s.coordinates || null,
            addressComplement: s.addressComplement || '',
            instructions: s.instructions || '',
          }))
        : [];

      setFormData({
        parentOperationMode: existingTransfer.parentOperationId ? 'existing' : 'new',
        parentOperationId: existingTransfer.parentOperationId || '',
        departureAddress: departure.address || '',
        departureCoordinates: departure.coordinates || null,
        departureAddressComplement: departure.addressComplement || '',
        arrivalAddress: arrival.address || '',
        arrivalCoordinates: arrival.coordinates || null,
        arrivalAddressComplement: arrival.addressComplement || '',
        intermediateStops,
        vehicleType: existingTransfer.vehicleType || existingTransfer.plannedVehicleType || '',
        vehicleTypeOther: '',
        operationType: existingTransfer.operationType || '',
        operationTypeOther: '',
        merchandiseType: existingTransfer.merchandiseType || '',
        merchandiseTypeOther: '',
        containerType: existingTransfer.containerType || '',
        weight: existingTransfer.weight || 1,
        numberOfTrips: existingTransfer.numberOfTrips || 1,
        tripsNetWeights: existingTransfer.tripsNetWeights?.length
          ? existingTransfer.tripsNetWeights
          : Array(existingTransfer.numberOfTrips || 1).fill(null),
        numberOfTrucks: existingTransfer.assignments?.length || 1,
        maxTrucksPerTransfer: 10,
        maxTransfersToCreate: 50,
        // Pour duplication: date du jour, pour édition: date existante
        date: mode === 'duplicate' 
          ? new Date().toISOString().split('T')[0] 
          : existingTransfer.date || new Date().toISOString().split('T')[0],
        shift: existingTransfer.shift || 'day',
        plannedStartTime: existingTransfer.plannedStartTime || '06:00',
        plannedEndTime: existingTransfer.plannedEndTime || '18:00',
        notes: existingTransfer.notes || '',
        transferDates: existingTransfer.transferDates || []
      });

      // Charger les assignations existantes
      if (existingTransfer.assignments?.length > 0) {
        setAssignments(existingTransfer.assignments.map((a, i) => ({
          index: i,
          driver: a.driverId ? {
            id: a.driverId,
            firstName: a.driverName?.split(' ')[0] || '',
            lastName: a.driverName?.split(' ').slice(1).join(' ') || '',
            phone: a.driverPhone,
            source: a.driverSource || 'local'
          } : null,
          vehicle: a.vehicleId ? {
            id: a.vehicleId,
            registration: a.vehiclePlate,
            vehicleType: a.vehicleType,
            source: a.vehicleSource || 'local'
          } : null,
          isManualDriver: a.isManualDriver || false,
          isManualVehicle: a.isManualVehicle || false,
          manualDriverName: a.isManualDriver ? a.driverName : '',
          manualDriverPhone: a.isManualDriver ? a.driverPhone : '',
          manualVehiclePlate: a.isManualVehicle ? a.vehiclePlate : '',
          manualVehicleType: a.vehicleType || '',
          manualVehicleBrand: a.vehicleBrand || '',
          manualVehicleModel: a.vehicleModel || '',
          manualVehicleCapacity: a.vehicleCapacity || ''
        })));
      } else if (existingTransfer.plannedDriverId || existingTransfer.plannedVehiclePlate) {
        // Legacy format
        setAssignments([{
          index: 0,
          driver: existingTransfer.plannedDriverId ? {
            id: existingTransfer.plannedDriverId,
            firstName: existingTransfer.plannedDriverName?.split(' ')[0] || '',
            lastName: existingTransfer.plannedDriverName?.split(' ').slice(1).join(' ') || '',
            phone: existingTransfer.plannedDriverPhone,
            source: 'local'
          } : null,
          vehicle: existingTransfer.plannedVehiclePlate ? {
            registration: existingTransfer.plannedVehiclePlate,
            vehicleType: existingTransfer.plannedVehicleType,
            source: 'local'
          } : null,
          isManualDriver: false,
          isManualVehicle: !existingTransfer.plannedVehicleId,
          manualDriverName: '',
          manualDriverPhone: '',
          manualVehiclePlate: existingTransfer.plannedVehiclePlate || '',
          manualVehicleType: existingTransfer.plannedVehicleType || '',
          manualVehicleBrand: '',
          manualVehicleModel: '',
          manualVehicleCapacity: ''
        }]);
      } else {
        setAssignments([createEmptyAssignment(0)]);
      }
    } else {
      // Mode création
      setFormData({
        parentOperationMode: parentOperations.length > 0 ? 'existing' : 'new',
        parentOperationId: '',
        departureAddress: '',
        departureCoordinates: null,
        arrivalAddress: '',
        arrivalCoordinates: null,
        intermediateStops: [],
        vehicleType: '',
        vehicleTypeOther: '',
        operationType: '',
        operationTypeOther: '',
        merchandiseType: '',
        merchandiseTypeOther: '',
        containerType: '',
        weight: 1,
        numberOfTrips: 1,
        tripsNetWeights: [null],
        numberOfTrucks: 1,
        maxTrucksPerTransfer: 10,
        maxTransfersToCreate: 50,
        date: new Date().toISOString().split('T')[0],
        shift: 'day',
        plannedStartTime: '06:00',
        plannedEndTime: '18:00',
        notes: ''
      });
      setAssignments([createEmptyAssignment(0)]);
    }
  };

  const createEmptyAssignment = (index) => ({
    index,
    driver: null,
    vehicle: null,
    isManualDriver: false,
    isManualVehicle: false,
    manualDriverName: '',
    manualDriverPhone: '',
    contactPhone: user?.phone || '',
    manualVehiclePlate: '',
    manualVehicleType: formData?.vehicleType || '',
    manualVehicleBrand: '',
    manualVehicleModel: '',
    manualVehicleCapacity: ''
  });

  // Fetch resources
  const fetchResources = useCallback(async () => {
    setLoadingResources(true);
    try {
      const [driversRes, vehiclesRes] = await Promise.all([
        // Récupère les chauffeurs disponibles et occupés (inclut les manuels)
        axios.get(`${API}/drivers?limit=200`),
        // Récupère les véhicules disponibles et occupés (inclut les manuels)
        axios.get(`${API}/vehicles?limit=200`)
      ]);
      
      // Filtrer pour n'avoir que les chauffeurs disponibles/occupés
      const drivers = (driversRes.data.items || driversRes.data || [])
        .filter(d => ['disponible', 'occupe', 'approved'].includes(d.status))
        .map(d => ({ 
          ...d, 
          source: d.source || 'local',
          displayName: `${d.firstName || ''} ${d.lastName || ''}`.trim() || d.phone
        }));
      
      // Filtrer pour n'avoir que les véhicules disponibles/occupés
      const vehicles = (vehiclesRes.data.items || vehiclesRes.data || [])
        .filter(v => ['disponible', 'occupe', 'active'].includes(v.status))
        .map(v => ({ 
          ...v, 
          source: v.source || 'local'
        }));
      
      setLocalDrivers(drivers);
      setLocalVehicles(vehicles);
    } catch (error) {
      console.error('Error fetching local resources:', error);
    } finally {
      setLoadingResources(false);
    }
  }, []);

  const fetchExternalResources = useCallback(async () => {
    setLoadingExternal(true);
    try {
      const [driversRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/external/drivers?limit=200&available_only=true`).catch(() => ({ data: { drivers: [] } })),
        axios.get(`${API}/external/vehicles?limit=200&available_only=true`).catch(() => ({ data: { vehicles: [] } }))
      ]);
      
      const extDrivers = (driversRes.data.drivers || []).map(d => ({
        id: d.id,
        firstName: d.nom?.split(' ')[0] || d.nom || '',
        lastName: d.nom?.split(' ').slice(1).join(' ') || '',
        phone: d.telephone || '',
        matricule: d.matricule,
        source: 'external'
      }));
      
      const extVehicles = (vehiclesRes.data.vehicles || []).map(v => ({
        id: v.id,
        registration: v.immatriculation || v.plate,
        vehicleType: v.type_camion || v.type,
        capacity: v.capacite_tonnes || v.capacity,
        brand: v.marque,
        model: v.modele,
        source: 'external'
      }));
      
      setExternalDrivers(extDrivers);
      setExternalVehicles(extVehicles);
    } catch (error) {
      console.error('Error fetching external resources:', error);
    } finally {
      setLoadingExternal(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && (currentStep === 2 || currentStep === 3)) {
      fetchResources();
      fetchExternalResources();
    }
  }, [isOpen, currentStep, fetchResources, fetchExternalResources]);

  // Charger les opérations disponibles au démarrage
  useEffect(() => {
    if (isOpen) {
      fetchAvailableOperations();
    }
  }, [isOpen, fetchAvailableOperations]);

  // Calculate distance — supporte multi-stops + récupère la geometry pour l'aperçu carte
  const calculateDistance = useCallback(async () => {
    if (!formData.departureCoordinates || !formData.arrivalCoordinates) {
      setRouteGeometry([]);
      setRouteMeta(null);
      return;
    }
    try {
      setRouteLoading(true);
      const stops = [];
      stops.push({ lat: formData.departureCoordinates.lat, lng: formData.departureCoordinates.lng });
      const intermediates = (formData.intermediateStops || [])
        .filter(s => s && s.coordinates && s.coordinates.lat && s.coordinates.lng)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      for (const s of intermediates) {
        stops.push({ lat: s.coordinates.lat, lng: s.coordinates.lng });
      }
      stops.push({ lat: formData.arrivalCoordinates.lat, lng: formData.arrivalCoordinates.lng });

      if (stops.length >= 2) {
        const response = await axios.post(`${API}/routing/multi-stop`, { stops });
        const data = response.data || {};
        // PM Spec 03/06/2026 — Aller-retour : doubler la distance (retour = même itinéraire).
        const multiplier = formData.tripType === 'round_trip' ? 2 : 1;
        const finalDistance = data.distance_km ? data.distance_km * multiplier : data.distance_km;
        setCalculatedDistance(finalDistance);
        setRouteGeometry(Array.isArray(data.coordinates) ? data.coordinates : []);
        setRouteMeta({
          distance_km: finalDistance,
          distance_one_way: data.distance_km,
          duration_s: data.duration_s ? data.duration_s * multiplier : data.duration_s,
          segments: data.segments,
          is_fallback: !!data.is_fallback,
          is_round_trip: formData.tripType === 'round_trip',
        });
      }
    } catch (error) {
      console.error('Error calculating distance:', error);
      setRouteGeometry([]);
      setRouteMeta(null);
    } finally {
      setRouteLoading(false);
    }
  }, [formData.departureCoordinates, formData.arrivalCoordinates, formData.intermediateStops, formData.tripType]);

  // Debounce 500ms : recalcul quand dep/arr/intermediates/tripType changent
  useEffect(() => {
    if (!formData.departureCoordinates || !formData.arrivalCoordinates) return;
    const t = setTimeout(() => calculateDistance(), 500);
    return () => clearTimeout(t);
  }, [formData.departureCoordinates, formData.arrivalCoordinates, formData.intermediateStops, formData.tripType, calculateDistance]);

  // Update form field
  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle address change
  const handleAddressChange = (field, value, coordinates = null) => {
    const coordField = field === 'departureAddress' ? 'departureCoordinates' : 'arrivalCoordinates';
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(coordinates ? { [coordField]: coordinates } : {})
    }));
  };

  // Handle parent operation selection with inheritance
  const handleParentOperationSelect = (opId) => {
    const op = parentOperations.find(o => o.id === opId);
    if (op) {
      updateForm('parentOperationId', opId);
      
      // Héritage des données
      if (op.departure) updateForm('departureAddress', op.departure);
      if (op.arrival) updateForm('arrivalAddress', op.arrival);
      if (op.vehicleType) updateForm('vehicleType', op.vehicleType);
      if (op.operationType) {
        updateForm('operationType', op.operationType);
        const selectedOp = operationTypes.find(t => t.value === op.operationType);
        setShowContainerOptions(selectedOp?.showContainerOption || false);
      }
      if (op.merchandiseType) updateForm('merchandiseType', op.merchandiseType);
      
      // Héritage nombre de camions et max
      const parentTrucks = op.numberOfTrucks || op.assignments?.length || 1;
      updateForm('numberOfTrucks', parentTrucks);
      updateForm('maxTrucksPerTransfer', parentTrucks);
      
      // Héritage des ressources
      const parentAssignments = op.assignments || [];
      if (parentAssignments.length > 0) {
        setAssignments(parentAssignments.map((a, i) => ({
          index: i,
          driver: a.driverId ? {
            id: a.driverId,
            firstName: a.driverName?.split(' ')[0] || '',
            lastName: a.driverName?.split(' ').slice(1).join(' ') || '',
            phone: a.driverPhone,
            source: a.driverSource || 'local'
          } : null,
          vehicle: a.vehicleId ? {
            id: a.vehicleId,
            registration: a.vehiclePlate,
            vehicleType: a.vehicleType,
            source: a.vehicleSource || 'local'
          } : null,
          isManualDriver: a.isManualDriver || false,
          isManualVehicle: a.isManualVehicle || false,
          manualDriverName: a.isManualDriver ? a.driverName : '',
          manualDriverPhone: a.isManualDriver ? a.driverPhone : '',
          manualVehiclePlate: a.isManualVehicle ? a.vehiclePlate : '',
          manualVehicleType: a.vehicleType || op.vehicleType || '',
          manualVehicleBrand: '',
          manualVehicleModel: '',
          manualVehicleCapacity: ''
        })));
        toast.success(`${parentAssignments.length} assignation(s) héritée(s) de l'opération mère`);
      } else if (op.assignedVehicle || op.assignedDriver) {
        // Legacy format
        setAssignments([{
          index: 0,
          driver: op.assignedDriver?.id ? {
            id: op.assignedDriver.id,
            firstName: op.assignedDriver.firstName || op.assignedDriver.name?.split(' ')[0] || '',
            lastName: op.assignedDriver.lastName || op.assignedDriver.name?.split(' ').slice(1).join(' ') || '',
            phone: op.assignedDriver.phone,
            source: 'local'
          } : null,
          vehicle: op.assignedVehicle?.plate ? {
            registration: op.assignedVehicle.plate,
            vehicleType: op.assignedVehicle.type || op.vehicleType,
            source: 'local'
          } : null,
          isManualDriver: false,
          isManualVehicle: !op.assignedVehicle?.id,
          manualDriverName: '',
          manualDriverPhone: '',
          manualVehiclePlate: op.assignedVehicle?.plate || '',
          manualVehicleType: op.assignedVehicle?.type || op.vehicleType || '',
          manualVehicleBrand: '',
          manualVehicleModel: '',
          manualVehicleCapacity: ''
        }]);
        toast.success("Ressources héritées de l'opération mère");
      }
    }
  };

  // Handle number of trucks change
  const handleNumberOfTrucksChange = (value) => {
    const num = Math.min(Math.max(1, parseInt(value) || 1), formData.maxTrucksPerTransfer);
    updateForm('numberOfTrucks', num);
    
    // Ajuster les assignations
    if (num > assignments.length) {
      const newAssignments = [...assignments];
      for (let i = assignments.length; i < num; i++) {
        newAssignments.push(createEmptyAssignment(i));
      }
      setAssignments(newAssignments);
    } else if (num < assignments.length) {
      setAssignments(assignments.slice(0, num));
    }
  };

  // Handle operation type change
  const handleOperationTypeChange = (value) => {
    updateForm('operationType', value);
    const selectedOp = operationTypes.find(op => op.value === value);
    setShowContainerOptions(selectedOp?.showContainerOption || false);
    
    if (selectedOp?.showContainerOption && !formData.containerType) {
      updateForm('containerType', containerTypes[0]?.value || '');
    } else if (!selectedOp?.showContainerOption) {
      updateForm('containerType', '');
    }
  };

  // Filtered resources
  const getFilteredDrivers = () => {
    let drivers = [];
    if (driverSource === 'all' || driverSource === 'local') drivers = [...drivers, ...localDrivers];
    if (driverSource === 'all' || driverSource === 'external') drivers = [...drivers, ...externalDrivers];
    
    if (driverSearch) {
      const search = driverSearch.toLowerCase();
      drivers = drivers.filter(d => {
        const name = `${d.firstName || ''} ${d.lastName || ''} ${d.nom || ''}`.toLowerCase();
        const phone = d.phone || d.telephone || '';
        const matricule = d.matricule || '';
        return name.includes(search) || phone.includes(search) || matricule.toLowerCase().includes(search);
      });
    }
    
    const selectedIds = assignments.filter(a => a.driver).map(a => a.driver.id);
    return drivers.filter(d => !selectedIds.includes(d.id));
  };

  const getFilteredVehicles = () => {
    let vehicles = [];
    if (vehicleSource === 'all' || vehicleSource === 'local') vehicles = [...vehicles, ...localVehicles];
    if (vehicleSource === 'all' || vehicleSource === 'external') vehicles = [...vehicles, ...externalVehicles];
    
    if (vehicleSearch) {
      const search = vehicleSearch.toLowerCase();
      vehicles = vehicles.filter(v => {
        const plate = v.registration || v.plate || v.plateNumber || '';
        const type = v.vehicleType || v.type || '';
        return plate.toLowerCase().includes(search) || type.toLowerCase().includes(search);
      });
    }
    
    const selectedIds = assignments.filter(a => a.vehicle).map(a => a.vehicle.id);
    return vehicles.filter(v => !selectedIds.includes(v.id));
  };

  // Assignment handlers
  const selectDriver = (idx, driver) => {
    setAssignments(prev => prev.map((a, i) => i === idx ? { ...a, driver, isManualDriver: false } : a));
  };

  const removeDriver = (idx) => {
    setAssignments(prev => prev.map((a, i) => i === idx ? { ...a, driver: null } : a));
  };

  const selectVehicle = (idx, vehicle) => {
    setAssignments(prev => prev.map((a, i) => i === idx ? { 
      ...a, 
      vehicle, 
      isManualVehicle: false,
      manualVehiclePlate: vehicle.registration || vehicle.plate || vehicle.plateNumber || '',
      manualVehicleType: vehicle.vehicleType || vehicle.type || ''
    } : a));
  };

  const removeVehicle = (idx) => {
    setAssignments(prev => prev.map((a, i) => i === idx ? { ...a, vehicle: null, manualVehiclePlate: '' } : a));
  };

  const toggleManualDriver = (idx, checked) => {
    setAssignments(prev => prev.map((a, i) => i === idx ? { ...a, isManualDriver: checked, driver: checked ? null : a.driver } : a));
  };

  const toggleManualVehicle = (idx, checked) => {
    setAssignments(prev => prev.map((a, i) => i === idx ? { ...a, isManualVehicle: checked, vehicle: checked ? null : a.vehicle } : a));
  };

  const updateAssignment = (idx, field, value) => {
    setAssignments(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  // Validation
  const validateStep = (step) => {
    switch (step) {
      case 0: // Trajet
        if (!formData.departureAddress || !formData.arrivalAddress) {
          toast.error('Veuillez renseigner les adresses de départ et d\'arrivée');
          return false;
        }
        return true;
      case 1: // Détails
        if (!formData.vehicleType) {
          toast.error('Veuillez sélectionner un type de véhicule');
          return false;
        }
        if (!formData.operationType) {
          toast.error('Veuillez sélectionner une nature d\'opération');
          return false;
        }
        if (showContainerOptions && !formData.containerType) {
          toast.error('Veuillez sélectionner un type de conteneur');
          return false;
        }
        if (!showContainerOptions && !formData.merchandiseType) {
          toast.error('Veuillez sélectionner un type de marchandise');
          return false;
        }
        if (formData.numberOfTrucks < 1) {
          toast.error('Le nombre de camions doit être au moins 1');
          return false;
        }
        return true;
      case 2: // Chauffeurs - optionnel
        return true;
      case 3: // Véhicules - optionnel
        return true;
      case 4: // Planning
        // Mode édition: date simple requise
        if (mode === 'edit') {
          if (!formData.date) {
            toast.error('Veuillez sélectionner une date');
            return false;
          }
        } else {
          // Mode création: au moins un transfert planifié via le calendrier
          if ((!formData.plannedTransfers || formData.plannedTransfers.length === 0) && !formData.date) {
            toast.error('Veuillez planifier au moins un transfert en cliquant sur une date du calendrier');
            return false;
          }
        }
        return true;
      default:
        return true;
    }
  };

  // Navigation
  const goNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  const goBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  // Submit
  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Build assignments data
      const assignmentsData = assignments
        .filter(a => a.driver || a.isManualDriver || a.vehicle || a.isManualVehicle || a.manualVehiclePlate)
        .map((a, idx) => ({
          index: idx,
          driverId: a.driver?.id || null,
          driverName: a.driver ? `${a.driver.firstName || ''} ${a.driver.lastName || ''}`.trim() : a.manualDriverName,
          driverPhone: a.driver?.phone || a.driver?.telephone || a.manualDriverPhone,
          contactPhone: a.contactPhone || user?.phone || null,
          driverSource: a.driver?.source || (a.isManualDriver ? 'manual' : 'local'),
          isManualDriver: a.isManualDriver || !a.driver,
          vehicleId: a.vehicle?.id || null,
          vehiclePlate: a.vehicle?.registration || a.vehicle?.plate || a.vehicle?.plateNumber || a.manualVehiclePlate,
          vehicleType: a.vehicle?.vehicleType || a.vehicle?.type || a.manualVehicleType || formData.vehicleType,
          vehicleBrand: a.vehicle?.brand || a.manualVehicleBrand || null,
          vehicleModel: a.vehicle?.model || a.manualVehicleModel || null,
          vehicleCapacity: a.vehicle?.capacity != null ? String(a.vehicle.capacity) : (a.manualVehicleCapacity || null),
          vehicleSource: a.vehicle?.source || (a.isManualVehicle ? 'manual' : 'local'),
          isManualVehicle: a.isManualVehicle || !a.vehicle
        }));

      // Convertir plannedTransfers au format backend
      // Le backend attend transferDates: ["2026-02-13", "2026-02-14"] et shift pour chaque
      let transferDatesForBackend = null;
      let primaryShift = formData.shift;
      
      if (formData.plannedTransfers && formData.plannedTransfers.length > 0 && mode !== 'edit') {
        // Nouveau format avec calendrier interactif
        // Trier par date et shift
        const sortedTransfers = [...formData.plannedTransfers].sort((a, b) => {
          if (a.date !== b.date) return a.date.localeCompare(b.date);
          return a.shift === 'day' ? -1 : 1;
        });
        
        transferDatesForBackend = sortedTransfers.map(t => ({
          date: t.date,
          shift: t.shift
        }));
        
        // Utiliser le shift du premier transfert comme principal
        primaryShift = sortedTransfers[0]?.shift || 'day';
      } else if (formData.numberOfTrucks > 1 && formData.transferDates?.length > 0) {
        // Format legacy avec transferDates simple (dates seulement)
        transferDatesForBackend = formData.transferDates.map(date => ({
          date: date,
          shift: formData.shift
        }));
      }

      const numberOfTransfers = transferDatesForBackend?.length || formData.numberOfTrucks || 1;

      const payload = {
        contractId: contractId,
        date: transferDatesForBackend?.[0]?.date || formData.date,
        // Nouveau format: array d'objets {date, shift}
        transferDates: transferDatesForBackend,
        shift: primaryShift,
        plannedStartTime: formData.plannedStartTime,
        plannedEndTime: formData.plannedEndTime,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        numberOfTrips: formData.numberOfTrips || 1,
        tripsNetWeights: formData.tripsNetWeights?.filter(w => w !== null && w !== '') || [],
        netWeight: formData.tripsNetWeights
          ? formData.tripsNetWeights.reduce((sum, w) => sum + (parseFloat(w) || 0), 0) || null
          : null,
        vehicleType: formData.vehicleType,
        vehicleTypeOther: formData.vehicleType === 'autre' ? formData.vehicleTypeOther : null,
        operationType: formData.operationType,
        operationTypeOther: formData.operationType === 'operation_speciale' ? formData.operationTypeOther : null,
        merchandiseType: showContainerOptions ? null : formData.merchandiseType,
        merchandiseTypeOther: formData.merchandiseType === 'autre' ? formData.merchandiseTypeOther : null,
        containerType: showContainerOptions ? formData.containerType : null,
        notes: formData.notes || null,
        numberOfTrucks: numberOfTransfers,
        assignments: assignmentsData,
        // Aller-retour & multi-stop (PM Spec 05/05/2026)
        tripType: formData.tripType || 'one_way',
        returnCargoMode: formData.tripType === 'round_trip' ? formData.returnCargoMode : null,
        returnMerchandiseDescription: formData.tripType === 'round_trip' ? (formData.returnMerchandiseDescription || null) : null,
        returnWeight: formData.tripType === 'round_trip' && formData.returnWeight ? Number(formData.returnWeight) : null,
        returnDepartureDate: formData.tripType === 'round_trip' ? (formData.returnDepartureDate || null) : null,
        distance_km: calculatedDistance || formData.distance_km || null,
        stops: [
          {
            address: formData.departureAddress,
            coordinates: formData.departureCoordinates,
            addressComplement: formData.departureAddressComplement || null,
            stopType: 'LOAD',
            isBillable: false,
            status: 'pending'
          },
          // Stops intermédiaires (multi-stop)
          ...(formData.intermediateStops || []).map((s, idx) => ({
            address: s.address,
            coordinates: s.coordinates || null,
            addressComplement: s.addressComplement || null,
            stopType: 'INTERMEDIATE',
            isBillable: !!s.isBillable,
            status: 'pending',
          })),
          {
            address: formData.arrivalAddress,
            coordinates: formData.arrivalCoordinates,
            addressComplement: formData.arrivalAddressComplement || null,
            stopType: 'DELIVERY',
            isBillable: true,
            status: 'pending'
          }
        ]
      };

      // Parent operation
      if (formData.parentOperationMode === 'existing' && formData.parentOperationId) {
        payload.parentOperationId = formData.parentOperationId;
        payload.parentOperationMode = 'existing';
        payload.useParentVehicle = assignmentsData.length === 0;
        payload.useParentDriver = assignmentsData.length === 0;
      } else {
        // Mode nouvelle opération OU mode existing sans sélection : le backend créera automatiquement l'opération mère
        payload.parentOperationMode = 'new';
      }

      // Assign primary resources for backward compatibility
      if (assignmentsData.length > 0) {
        const primary = assignmentsData[0];
        payload.plannedVehicleId = primary.vehicleId;
        payload.plannedVehiclePlate = primary.vehiclePlate;
        payload.plannedVehicleType = primary.vehicleType;
        payload.plannedDriverId = primary.driverId;
        payload.plannedDriverName = primary.driverName;
        payload.plannedDriverPhone = primary.driverPhone;
      }

      if (mode === 'edit' && existingTransfer?.id) {
        // Mode édition - PATCH
        await axios.patch(`${API}/contract-runs/${existingTransfer.id}`, payload);
        toast.success(`${terminology} mis à jour avec succès`);
      } else if (mode === 'duplicate' && existingTransfer?.id) {
        // Mode duplication - utiliser l'endpoint dédié qui génère une nouvelle référence correcte
        const response = await axios.post(
          `${API}/contract-runs/${existingTransfer.id}/duplicate`,
          {}
        );
        const newRun = response.data?.run;
        const newRef = newRun?.shortReference || newRun?.runNumber || '';
        toast.success(`Transfert dupliqué avec succès${newRef ? ` — Référence : ${newRef}` : ''}`);
      } else {
        // Mode création - POST
        const response = await axios.post(`${API}/contracts/${contractId}/runs`, payload);
        const createdCount = response.data?.created_count || numberOfTransfers;
        toast.success(`${createdCount} transfert${createdCount > 1 ? 's' : ''} créé${createdCount > 1 ? 's' : ''} avec succès`);

        // Save as template prompt (PM Spec 05/05/2026)
        // Affiché uniquement si pas issu d'un template ET au moins le trajet est rempli
        if (!formData.templateId && formData.departureAddress && formData.arrivalAddress) {
          // Stocke localement les infos pour proposer la sauvegarde via state
          setPendingTemplateSnapshot({
            departureAddress: formData.departureAddress,
            departureCoordinates: formData.departureCoordinates,
            arrivalAddress: formData.arrivalAddress,
            arrivalCoordinates: formData.arrivalCoordinates,
            vehicleType: formData.vehicleType,
            operationType: formData.operationType,
            merchandiseType: formData.merchandiseType,
            containerType: formData.containerType,
            weight: formData.weight,
            numberOfTrucks: formData.numberOfTrucks,
            shift: formData.shift,
            plannedStartTime: formData.plannedStartTime,
            plannedEndTime: formData.plannedEndTime,
            tripType: formData.tripType,
            returnCargoMode: formData.returnCargoMode,
            returnMerchandiseDescription: formData.returnMerchandiseDescription,
            returnWeight: formData.returnWeight ? Number(formData.returnWeight) : null,
            stops: (formData.intermediateStops || []).map(s => ({
              address: s.address, coordinates: s.coordinates,
              addressComplement: s.addressComplement,
            })),
            distance_km: calculatedDistance ? Math.round(calculatedDistance) : null,
            notes: formData.notes,
          });
          setShowSaveAsTemplate(true);
          // On NE ferme PAS la modale tout de suite — l'utilisateur fait son choix
          return;
        }
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error saving transfer:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  // Labels helpers
  const getVehicleTypeLabel = (value) => vehicleTypes.find(v => v.id === value)?.name || value;
  const getOperationTypeLabel = (value) => operationTypes.find(o => o.value === value)?.label?.replace(/^[^\s]+\s/, '') || value;
  const getMerchandiseTypeLabel = (value) => merchandiseTypes.find(m => m.value === value)?.label?.replace(/^[^\s]+\s/, '') || value;
  const getContainerTypeLabel = (value) => containerTypes.find(c => c.value === value)?.label || value;

  // Save as Template handler (PM Spec 05/05/2026)
  const handleSaveAsTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Saisissez un nom pour le template');
      return;
    }
    setSavingTemplate(true);
    try {
      await axios.post(`${API}/templates`, {
        ...pendingTemplateSnapshot,
        name: newTemplateName.trim(),
        type: 'transfer',
      });
      toast.success(`Template « ${newTemplateName.trim()} » enregistré`);
      setShowSaveAsTemplate(false);
      setNewTemplateName('');
      setPendingTemplateSnapshot(null);
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur sauvegarde template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSkipSaveTemplate = () => {
    setShowSaveAsTemplate(false);
    setPendingTemplateSnapshot(null);
    setNewTemplateName('');
    onSuccess?.();
    onClose();
  };

  const getSourceBadge = (source) => {
    if (source === 'manual') return <Badge className="bg-amber-100 text-amber-800 border-0 text-xs"><Plus className="w-3 h-3 mr-1" />Manuel</Badge>;
    if (source === 'local') return <Badge className="bg-emerald-100 text-emerald-800 border-0 text-xs"><Users className="w-3 h-3 mr-1" />Local</Badge>;
    return <Badge className="bg-blue-100 text-blue-800 border-0 text-xs"><Database className="w-3 h-3 mr-1" />Truck DB</Badge>;
  };

  // Step content renders
  const renderStep0 = () => (
    <div className="grid lg:grid-cols-[1fr_minmax(360px,460px)] gap-6">
      {/* Colonne gauche : formulaire trajet */}
      <div className="space-y-6 min-w-0">
        {/* Distance badge */}
        {calculatedDistance && (
          <div className="flex justify-end lg:hidden">
            <Badge className="bg-emerald-100 text-emerald-800 text-sm px-3 py-1">
              Distance: {Math.round(calculatedDistance)} km
            </Badge>
          </div>
        )}

      {/* Template Selector */}
      {mode === 'create' && templates.length > 0 && (
        <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
          <Label className="text-sm font-medium mb-3 block flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            Utiliser un template
          </Label>
          <Select 
            value={selectedTemplate?.id || undefined}
            onValueChange={(value) => {
              const template = templates.find(t => t.id === value);
              applyTemplate(template);
            }}
          >
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="Sélectionner un template pré-configuré..." />
            </SelectTrigger>
            <SelectContent>
              {templates.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  <div className="flex items-center gap-2">
                    <Route className="w-4 h-4 text-blue-500" />
                    <span className="font-medium">{template.name}</span>
                    {template.departureAddress && template.arrivalAddress && (
                      <span className="text-gray-500 text-xs ml-2">
                        ({template.departureAddress.split(',')[0]} → {template.arrivalAddress.split(',')[0]})
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedTemplate && (
            <p className="text-xs text-blue-600 mt-2">
              Template "{selectedTemplate.name}" appliqué - vous pouvez modifier les valeurs ci-dessous
            </p>
          )}
        </div>
      )}

      {/* Parent Operation - Nouveau composant OperationSelector */}
      <div className="p-4 border rounded-lg bg-gray-50" data-testid="operation-selector-section">
        <Label className="text-sm font-medium mb-3 block">Opération principale</Label>
        <OperationSelector
          contractId={contractId}
          contractNumber={contractNumber}
          selectedOperationId={formData.parentOperationId}
          mode={formData.parentOperationMode}
          departureAddress={formData.departureAddress}
          arrivalAddress={formData.arrivalAddress}
          onSelect={(operation) => {
            if (operation) {
              // Update parentOperationId and inherit data from operation
              updateForm('parentOperationId', operation.id);
              
              // Héritage des données
              if (operation.departure) updateForm('departureAddress', operation.departure);
              if (operation.arrival) updateForm('arrivalAddress', operation.arrival);
              if (operation.vehicleType) updateForm('vehicleType', operation.vehicleType);
              if (operation.operationType) {
                updateForm('operationType', operation.operationType);
                const selectedOp = operationTypes.find(t => t.value === operation.operationType);
                setShowContainerOptions(selectedOp?.showContainerOption || false);
              }
              if (operation.merchandiseType) updateForm('merchandiseType', operation.merchandiseType);
              
              // Héritage nombre de camions et max
              const parentTrucks = operation.numberOfTrucks || operation.assignments?.length || 1;
              updateForm('numberOfTrucks', parentTrucks);
              updateForm('maxTrucksPerTransfer', parentTrucks);
              
              // Héritage des ressources
              const parentAssignments = operation.assignments || [];
              if (parentAssignments.length > 0) {
                setAssignments(parentAssignments.map((a, i) => ({
                  index: i,
                  driver: a.driverId ? {
                    id: a.driverId,
                    firstName: a.driverName?.split(' ')[0] || '',
                    lastName: a.driverName?.split(' ').slice(1).join(' ') || '',
                    phone: a.driverPhone,
                    source: a.driverSource || 'local'
                  } : null,
                  vehicle: a.vehicleId ? {
                    id: a.vehicleId,
                    registration: a.vehiclePlate,
                    vehicleType: a.vehicleType,
                    source: a.vehicleSource || 'local'
                  } : null,
                  isManualDriver: a.isManualDriver || false,
                  isManualVehicle: a.isManualVehicle || false,
                  manualDriverName: a.isManualDriver ? a.driverName : '',
                  manualDriverPhone: a.isManualDriver ? a.driverPhone : '',
                  manualVehiclePlate: a.isManualVehicle ? a.vehiclePlate : '',
                  manualVehicleType: a.vehicleType || operation.vehicleType || '',
                  manualVehicleBrand: '',
                  manualVehicleModel: '',
                  manualVehicleCapacity: ''
                })));
                toast.success(`${parentAssignments.length} assignation(s) héritée(s) de l'opération mère`);
              } else if (operation.assignedVehicle || operation.assignedDriver) {
                // Legacy format
                setAssignments([{
                  index: 0,
                  driver: operation.assignedDriver?.id ? {
                    id: operation.assignedDriver.id,
                    firstName: operation.assignedDriver.firstName || operation.assignedDriver.name?.split(' ')[0] || '',
                    lastName: operation.assignedDriver.lastName || operation.assignedDriver.name?.split(' ').slice(1).join(' ') || '',
                    phone: operation.assignedDriver.phone,
                    source: 'local'
                  } : null,
                  vehicle: operation.assignedVehicle?.plate ? {
                    registration: operation.assignedVehicle.plate,
                    vehicleType: operation.assignedVehicle.type || operation.vehicleType,
                    source: 'local'
                  } : null,
                  isManualDriver: false,
                  isManualVehicle: !operation.assignedVehicle?.id,
                  manualDriverName: '',
                  manualDriverPhone: '',
                  manualVehiclePlate: operation.assignedVehicle?.plate || '',
                  manualVehicleType: operation.assignedVehicle?.type || operation.vehicleType || '',
                  manualVehicleBrand: '',
                  manualVehicleModel: '',
                  manualVehicleCapacity: ''
                }]);
                toast.success("Ressources héritées de l'opération mère");
              }
            }
          }}
          onModeChange={(newMode) => {
            updateForm('parentOperationMode', newMode);
            if (newMode === 'new') {
              updateForm('parentOperationId', '');
              updateForm('maxTrucksPerTransfer', 10);
            }
          }}
        />
      </div>

      {/* Departure */}
      <div>
        <Label className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">D</div>
          Adresse de départ *
        </Label>
        <AddressInput
          value={formData.departureAddress}
          onChange={(value, coords) => handleAddressChange('departureAddress', value, coords)}
          placeholder="Ex: Abidjan, Port Autonome, Côte d'Ivoire"
        />
      </div>

      {/* Étapes intermédiaires (multi-villes passage) — PM Spec 01/06/2026 */}
      <div data-testid="transfer-multi-stops-section">
        <Label className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold">…</div>
          Étapes intermédiaires (optionnel)
        </Label>
        <p className="text-xs text-gray-500 mb-3">
          Ajoutez les villes traversées entre le départ et l'arrivée. Le chauffeur pourra valider chaque étape sur le terrain.
        </p>
        <MultiStopInput
          stops={formData.intermediateStops || []}
          onStopsChange={(newStops) => updateForm('intermediateStops', newStops)}
          maxStops={8}
        />
      </div>

      {/* Arrival */}
      <div>
        <Label className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold">A</div>
          Adresse d'arrivée *
        </Label>
        <AddressInput
          value={formData.arrivalAddress}
          onChange={(value, coords) => handleAddressChange('arrivalAddress', value, coords)}
          placeholder="Ex: Ouagadougou, Zone Industrielle, Burkina Faso"
        />
      </div>

      {/* Type de trajet (PM Spec 05/05/2026) */}
      <div className="space-y-3 pt-2">
        <Label className="font-semibold">Type de trajet</Label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => updateForm('tripType', 'one_way')}
            className={cn(
              'px-4 py-2 rounded-full text-sm font-medium border transition-colors flex items-center gap-2',
              formData.tripType === 'one_way'
                ? 'bg-white border-slate-300 text-slate-800 shadow-sm'
                : 'bg-slate-50 border-transparent text-slate-500 hover:bg-slate-100'
            )}
            data-testid="trip-type-one-way"
          >
            → Aller simple
          </button>
          <button
            type="button"
            onClick={() => updateForm('tripType', 'round_trip')}
            className={cn(
              'px-4 py-2 rounded-full text-sm font-medium border transition-colors flex items-center gap-2',
              formData.tripType === 'round_trip'
                ? 'bg-white border-slate-300 text-slate-800 shadow-sm'
                : 'bg-slate-50 border-transparent text-slate-500 hover:bg-slate-100'
            )}
            data-testid="trip-type-round-trip"
          >
            ↻ Aller-retour
          </button>
        </div>
      </div>

      {/* Bloc retour si aller-retour */}
      {formData.tripType === 'round_trip' && (
        <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50/30" data-testid="round-trip-details">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600">
            ↻ Détails du retour
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Mode du retour</Label>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => updateForm('returnCargoMode', 'empty')}
                  className={cn(
                    'flex-1 px-3 py-2 rounded-md text-xs font-medium border',
                    formData.returnCargoMode === 'empty'
                      ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  )}
                  data-testid="return-cargo-empty"
                >
                  Vide
                </button>
                <button
                  type="button"
                  onClick={() => updateForm('returnCargoMode', 'loaded')}
                  className={cn(
                    'flex-1 px-3 py-2 rounded-md text-xs font-medium border',
                    formData.returnCargoMode === 'loaded'
                      ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  )}
                  data-testid="return-cargo-loaded"
                >
                  Chargé
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Date départ retour (optionnel)</Label>
              <Input
                type="date"
                value={formData.returnDepartureDate || ''}
                onChange={(e) => updateForm('returnDepartureDate', e.target.value)}
                className="h-9 text-sm mt-1"
                data-testid="return-departure-date"
              />
            </div>
          </div>
          {formData.returnCargoMode === 'loaded' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Marchandise retour</Label>
                <Input
                  value={formData.returnMerchandiseDescription || ''}
                  onChange={(e) => updateForm('returnMerchandiseDescription', e.target.value)}
                  placeholder='Ex: "Idem aller" ou désignation propre'
                  className="h-9 text-sm mt-1"
                  data-testid="return-merchandise-description"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Poids retour (T)</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.returnWeight || ''}
                  onChange={(e) => updateForm('returnWeight', e.target.value)}
                  placeholder="Ex: 5"
                  className="h-9 text-sm mt-1"
                  data-testid="return-weight"
                />
              </div>
            </div>
          )}
        </div>
      )}
      </div>
      {/* Fin colonne gauche */}

      {/* Colonne droite : aperçu carte sticky */}
      <div className="lg:sticky lg:top-2 lg:self-start">
        <RoutePreviewMap
          departure={
            formData.departureCoordinates
              ? { ...formData.departureCoordinates, address: formData.departureAddress }
              : null
          }
          arrival={
            formData.arrivalCoordinates
              ? { ...formData.arrivalCoordinates, address: formData.arrivalAddress }
              : null
          }
          intermediates={formData.intermediateStops || []}
          polyline={routeGeometry}
          meta={routeMeta}
          loading={routeLoading}
          height="500px"
        />
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Distance badge */}
      {calculatedDistance && (
        <div className="flex justify-end">
          <Badge className="bg-emerald-100 text-emerald-800 text-sm px-3 py-1">
            Distance: {Math.round(calculatedDistance)} km
          </Badge>
        </div>
      )}

      {/* Vehicle Type */}
      <div className="space-y-3">
        <Label className="font-semibold">Type de véhicule *</Label>
        <VehicleSelector
          vehicles={vehicleTypes}
          selected={formData.vehicleType}
          onSelect={(value) => updateForm('vehicleType', value)}
        />
        {formData.vehicleType === 'autre' && (
          <Input
            value={formData.vehicleTypeOther}
            onChange={(e) => updateForm('vehicleTypeOther', e.target.value)}
            placeholder="Précisez le type de véhicule..."
            className="mt-2"
          />
        )}
      </div>

      {/* Operation Type */}
      <div className="space-y-2">
        <Label>Nature de l'opération *</Label>
        <Select value={formData.operationType || undefined} onValueChange={handleOperationTypeChange}>
          <SelectTrigger>
            <SelectValue placeholder="-- Sélectionnez --" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {operationTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Container Type */}
      {showContainerOptions && (
        <div className="space-y-3 bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            <Label className="font-semibold">Type de conteneur *</Label>
          </div>
          <Select value={formData.containerType || undefined} onValueChange={(v) => updateForm('containerType', v)}>
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="-- Choisissez un type de conteneur --" />
            </SelectTrigger>
            <SelectContent className="max-h-[400px]">
              {containerTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{type.icon}</span>
                    <div>
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-gray-500">{type.dimensions}</div>
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Merchandise Type */}
      {!showContainerOptions && (
        <div className="space-y-2">
          <Label>Type de marchandise *</Label>
          <Select value={formData.merchandiseType || undefined} onValueChange={(v) => updateForm('merchandiseType', v)}>
            <SelectTrigger>
              <SelectValue placeholder="-- Sélectionnez --" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {merchandiseTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Weight */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Weight className="w-4 h-4" />
          Poids estimé (tonnes)
        </Label>
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" size="icon" onClick={() => updateForm('weight', Math.max(1, (formData.weight || 1) - 1))} className="h-10 w-10">-</Button>
          <Input
            type="number"
            min="1"
            max="220"
            value={formData.weight}
            onChange={(e) => updateForm('weight', Math.min(220, Math.max(1, parseInt(e.target.value) || 1)))}
            className="w-24 text-center text-lg font-semibold"
          />
          <Button type="button" variant="outline" size="icon" onClick={() => updateForm('weight', Math.min(220, (formData.weight || 1) + 1))} className="h-10 w-10">+</Button>
          <span className="text-gray-500">tonne{formData.weight > 1 ? 's' : ''}</span>
        </div>
        <p className="text-xs text-gray-400">Poids approximatif prévu à la création</p>
      </div>

      {/* Nombre de tours (voyages aller-retour) */}
      <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <Label className="flex items-center gap-2 font-semibold text-blue-800">
          <RefreshCw className="w-4 h-4" />
          Nombre de tours (voyages aller-retour)
        </Label>
        <div className="flex items-center gap-3">
          <Button
            type="button" variant="outline" size="icon"
            onClick={() => {
              const newVal = Math.max(1, (formData.numberOfTrips || 1) - 1);
              updateForm('numberOfTrips', newVal);
              const trips = Array.from({ length: newVal }, (_, i) => formData.tripsNetWeights?.[i] ?? null);
              updateForm('tripsNetWeights', trips);
            }}
            disabled={(formData.numberOfTrips || 1) <= 1}
            className="h-10 w-10"
          >-</Button>
          <Input
            type="number" min="1" max="50"
            value={formData.numberOfTrips || 1}
            onChange={(e) => {
              const val = Math.min(50, Math.max(1, parseInt(e.target.value) || 1));
              updateForm('numberOfTrips', val);
              const trips = Array.from({ length: val }, (_, i) => formData.tripsNetWeights?.[i] ?? null);
              updateForm('tripsNetWeights', trips);
            }}
            className="w-20 text-center text-lg font-semibold"
          />
          <Button
            type="button" variant="outline" size="icon"
            onClick={() => {
              const newVal = Math.min(50, (formData.numberOfTrips || 1) + 1);
              updateForm('numberOfTrips', newVal);
              const trips = Array.from({ length: newVal }, (_, i) => formData.tripsNetWeights?.[i] ?? null);
              updateForm('tripsNetWeights', trips);
            }}
            className="h-10 w-10"
          >+</Button>
          <span className="text-blue-700 font-medium">tour{(formData.numberOfTrips || 1) > 1 ? 's' : ''}</span>
        </div>

        {/* Grille poids net par tour */}
        {(formData.numberOfTrips || 1) > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-blue-700 font-medium">Poids net réel par tour (tonnes) — à saisir après pesée :</p>
            <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto pr-1">
              {Array.from({ length: formData.numberOfTrips || 1 }, (_, i) => (
                <div key={i} className="flex items-center gap-2 bg-white rounded border border-blue-100 p-2">
                  <span className="text-xs text-blue-600 font-medium min-w-[50px]">Tour {i + 1}</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    placeholder="0.0"
                    value={formData.tripsNetWeights?.[i] ?? ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? null : parseFloat(e.target.value);
                      const updated = [...(formData.tripsNetWeights || Array(formData.numberOfTrips || 1).fill(null))];
                      updated[i] = val;
                      updateForm('tripsNetWeights', updated);
                    }}
                    className="h-7 text-sm text-center"
                  />
                  <span className="text-xs text-gray-500">T</span>
                </div>
              ))}
            </div>
            {formData.tripsNetWeights?.some(w => w !== null && w !== '') && (
              <div className="flex items-center gap-2 pt-1 border-t border-blue-200">
                <span className="text-xs text-blue-800 font-semibold">Total net :</span>
                <span className="text-sm font-bold text-blue-900">
                  {formData.tripsNetWeights.reduce((sum, w) => sum + (parseFloat(w) || 0), 0).toFixed(1)} T
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Number of Trucks */}
      <div className="space-y-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
        <Label className="flex items-center gap-2 font-semibold text-amber-800">
          <Truck className="w-5 h-5" />
          Nombre de camions
          {formData.parentOperationId && (
            <Badge className="text-xs bg-amber-100 text-amber-700">Max: {formData.maxTrucksPerTransfer}</Badge>
          )}
        </Label>
        <div className="flex items-center gap-3">
          <Button 
            type="button" 
            variant="outline" 
            size="icon" 
            onClick={() => handleNumberOfTrucksChange(formData.numberOfTrucks - 1)} 
            disabled={formData.numberOfTrucks <= 1}
            className="h-10 w-10"
          >-</Button>
          <Input
            type="number"
            min="1"
            max={formData.maxTrucksPerTransfer}
            value={formData.numberOfTrucks}
            onChange={(e) => handleNumberOfTrucksChange(parseInt(e.target.value) || 1)}
            className="w-24 text-center text-lg font-semibold"
          />
          <Button 
            type="button" 
            variant="outline" 
            size="icon" 
            onClick={() => handleNumberOfTrucksChange(formData.numberOfTrucks + 1)} 
            disabled={formData.numberOfTrucks >= formData.maxTrucksPerTransfer}
            className="h-10 w-10"
          >+</Button>
          <span className="text-amber-700">camion{formData.numberOfTrucks > 1 ? 's' : ''}</span>
        </div>
        <p className="text-xs text-amber-600 mt-1">
          {formData.parentOperationId 
            ? `Limité à ${formData.maxTrucksPerTransfer} camion(s) comme l'opération principale`
            : 'Vous pourrez assigner chauffeurs et véhicules à l\'étape suivante'}
        </p>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      {/* Distance badge */}
      {calculatedDistance && (
        <div className="flex justify-end mb-2">
          <Badge className="bg-emerald-100 text-emerald-800 text-sm px-3 py-1">
            Distance: {Math.round(calculatedDistance)} km
          </Badge>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <User className="w-5 h-5 text-emerald-600" />
          <h3 className="text-lg font-semibold">Sélection des Chauffeurs</h3>
        </div>
        <Badge variant="outline" className="text-lg px-3 py-1">
          {formData.numberOfTrucks} camion{formData.numberOfTrucks > 1 ? 's' : ''} demandé{formData.numberOfTrucks > 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Rechercher par nom, téléphone, matricule..."
            value={driverSearch}
            onChange={(e) => setDriverSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={driverSource} onValueChange={setDriverSource}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes sources</SelectItem>
            <SelectItem value="local">Local</SelectItem>
            <SelectItem value="external">Truck DB</SelectItem>
          </SelectContent>
        </Select>
        {(loadingResources || loadingExternal) && <Loader2 className="w-5 h-5 animate-spin text-blue-500" />}
      </div>

      {/* Assignments Grid */}
      <div className="space-y-4 max-h-[520px] overflow-y-auto">
        {assignments.map((assignment, idx) => (
          <div key={idx} className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-700">
                Camion #{idx + 1} {idx === 0 && <span className="text-xs text-gray-500">(Principal)</span>}
              </h4>
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`manual-driver-${idx}`}
                  checked={assignment.isManualDriver}
                  onCheckedChange={(checked) => toggleManualDriver(idx, checked)}
                />
                <Label htmlFor={`manual-driver-${idx}`} className="text-sm cursor-pointer">Saisie manuelle</Label>
              </div>
            </div>

            {!assignment.isManualDriver ? (
              <>
                {assignment.driver ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${assignment.driver.source === 'local' ? 'bg-emerald-500' : 'bg-blue-500'}`}>
                        {(assignment.driver.firstName?.charAt(0) || assignment.driver.nom?.charAt(0) || 'C')}
                      </div>
                      <div>
                        <p className="font-medium">{assignment.driver.firstName} {assignment.driver.lastName || assignment.driver.nom}</p>
                        <p className="text-sm text-gray-600">{assignment.driver.phone || assignment.driver.telephone}</p>
                      </div>
                      {getSourceBadge(assignment.driver.source)}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeDriver(idx)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ) : (
                  <Select onValueChange={(value) => {
                    const driver = [...localDrivers, ...externalDrivers].find(d => d.id === value);
                    if (driver) selectDriver(idx, driver);
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un chauffeur..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {getFilteredDrivers().length === 0 ? (
                        <div className="p-4 text-center text-gray-500">Aucun chauffeur disponible</div>
                      ) : (
                        getFilteredDrivers().map(driver => (
                          <SelectItem key={driver.id} value={driver.id}>
                            <div className="flex items-center gap-2">
                              {driver.source === 'external' ? <Database className="w-3 h-3 text-blue-500" /> : <Users className="w-3 h-3 text-emerald-500" />}
                              <span>{driver.firstName} {driver.lastName || driver.nom}</span>
                              <span className="text-gray-400">-</span>
                              <span className="text-gray-500">{driver.phone || driver.telephone}</span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                )}
              </>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nom *</Label>
                  <Input
                    placeholder="ex: Jean Kouassi"
                    value={assignment.manualDriverName}
                    onChange={(e) => updateAssignment(idx, 'manualDriverName', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Téléphone *</Label>
                  <Input
                    placeholder="ex: +225 07 XX XX XX"
                    value={assignment.manualDriverPhone}
                    onChange={(e) => updateAssignment(idx, 'manualDriverPhone', e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Contact Opérationnel affiché au client */}
            <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
              <Label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-1.5">
                <Phone className="w-3.5 h-3.5" />
                Numéro affiché au client
              </Label>
              <Input
                placeholder="ex: +225 05 64 73 09 09"
                value={assignment.contactPhone || ''}
                onChange={(e) => updateAssignment(idx, 'contactPhone', e.target.value)}
                className="text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                Pré-rempli avec votre numéro. Le client ne verra jamais le numéro direct du chauffeur.
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="flex gap-4 text-sm text-gray-600 pt-2">
        <span className="flex items-center gap-1"><Users className="w-3 h-3 text-emerald-500" /> {localDrivers.length} locaux</span>
        <span className="flex items-center gap-1"><Database className="w-3 h-3 text-blue-500" /> {externalDrivers.length} Truck DB</span>
        <span>✅ {assignments.filter(a => a.driver || a.manualDriverName).length}/{formData.numberOfTrucks} assignés</span>
      </div>

      <p className="text-xs text-gray-500 bg-gray-100 p-2 rounded">
        <AlertCircle className="w-3 h-3 inline mr-1" />
        Optionnel - Vous pourrez assigner les chauffeurs plus tard via la planification
      </p>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      {/* Distance badge */}
      {calculatedDistance && (
        <div className="flex justify-end mb-2">
          <Badge className="bg-emerald-100 text-emerald-800 text-sm px-3 py-1">
            Distance: {Math.round(calculatedDistance)} km
          </Badge>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-emerald-600" />
          <h3 className="text-lg font-semibold">Sélection des Véhicules</h3>
        </div>
        <Badge variant="outline">Type demandé: {getVehicleTypeLabel(formData.vehicleType)}</Badge>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Rechercher par plaque, type..."
            value={vehicleSearch}
            onChange={(e) => setVehicleSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={vehicleSource} onValueChange={setVehicleSource}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes sources</SelectItem>
            <SelectItem value="local">Local</SelectItem>
            <SelectItem value="external">Truck DB</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Assignments Grid */}
      <div className="space-y-4 max-h-[520px] overflow-y-auto">
        {assignments.map((assignment, idx) => (
          <div key={idx} className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-gray-700">Camion #{idx + 1}</h4>
                {assignment.driver && (
                  <span className="text-sm text-gray-500">({assignment.driver.firstName} {assignment.driver.lastName || assignment.driver.nom || assignment.manualDriverName})</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`manual-vehicle-${idx}`}
                  checked={assignment.isManualVehicle}
                  onCheckedChange={(checked) => toggleManualVehicle(idx, checked)}
                />
                <Label htmlFor={`manual-vehicle-${idx}`} className="text-sm cursor-pointer">Saisie manuelle</Label>
              </div>
            </div>

            {!assignment.isManualVehicle ? (
              <>
                {assignment.vehicle ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{vehicleTypes.find(v => v.id === (assignment.vehicle.vehicleType || assignment.vehicle.type))?.image || '🚛'}</span>
                      <div>
                        <p className="font-medium">{assignment.vehicle.registration || assignment.vehicle.plate || assignment.vehicle.plateNumber}</p>
                        <p className="text-sm text-gray-600">{assignment.vehicle.brand} {assignment.vehicle.model} • {assignment.vehicle.capacity}T</p>
                      </div>
                      {getSourceBadge(assignment.vehicle.source)}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeVehicle(idx)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ) : (
                  <Select onValueChange={(value) => {
                    const vehicle = [...localVehicles, ...externalVehicles].find(v => v.id === value);
                    if (vehicle) selectVehicle(idx, vehicle);
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un véhicule..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {getFilteredVehicles().length === 0 ? (
                        <div className="p-4 text-center text-gray-500">Aucun véhicule disponible</div>
                      ) : (
                        getFilteredVehicles().map(vehicle => (
                          <SelectItem key={vehicle.id} value={vehicle.id}>
                            <div className="flex items-center gap-2">
                              <span>{vehicleTypes.find(v => v.id === (vehicle.vehicleType || vehicle.type))?.image || '🚛'}</span>
                              {vehicle.source === 'external' ? <Database className="w-3 h-3 text-blue-500" /> : <Users className="w-3 h-3 text-emerald-500" />}
                              <span className="font-medium">{vehicle.registration || vehicle.plate || vehicle.plateNumber}</span>
                              <span className="text-gray-400">-</span>
                              <span className="text-gray-500">{vehicle.capacity}T</span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                )}
              </>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Plaque *</Label>
                    <Input
                      placeholder="ex: CI-1234-AB"
                      value={assignment.manualVehiclePlate}
                      onChange={(e) => updateAssignment(idx, 'manualVehiclePlate', e.target.value.toUpperCase())}
                    />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={assignment.manualVehicleType || undefined} onValueChange={(v) => updateAssignment(idx, 'manualVehicleType', v)}>
                      <SelectTrigger><SelectValue placeholder="Type..." /></SelectTrigger>
                      <SelectContent>
                        {vehicleTypes.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.image} {t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Marque</Label>
                    <Input placeholder="Mercedes" value={assignment.manualVehicleBrand} onChange={(e) => updateAssignment(idx, 'manualVehicleBrand', e.target.value)} />
                  </div>
                  <div>
                    <Label>Modèle</Label>
                    <Input placeholder="Actros" value={assignment.manualVehicleModel} onChange={(e) => updateAssignment(idx, 'manualVehicleModel', e.target.value)} />
                  </div>
                  <div>
                    <Label>Capacité (T)</Label>
                    <Input type="number" placeholder="25" value={assignment.manualVehicleCapacity} onChange={(e) => updateAssignment(idx, 'manualVehicleCapacity', e.target.value)} />
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="flex gap-4 text-sm text-gray-600 pt-2">
        <span className="flex items-center gap-1"><Users className="w-3 h-3 text-emerald-500" /> {localVehicles.length} locaux</span>
        <span className="flex items-center gap-1"><Database className="w-3 h-3 text-blue-500" /> {externalVehicles.length} Truck DB</span>
        <span>✅ {assignments.filter(a => a.vehicle || a.manualVehiclePlate).length}/{formData.numberOfTrucks} assignés</span>
      </div>

      <p className="text-xs text-gray-500 bg-gray-100 p-2 rounded">
        <AlertCircle className="w-3 h-3 inline mr-1" />
        Optionnel - Vous pourrez assigner les véhicules plus tard via la planification
      </p>
    </div>
  );

  const renderStep4 = () => {
    // Calculer le nombre de transferts depuis plannedTransfers
    const transferCount = formData.plannedTransfers?.length || 0;
    const today = new Date().toISOString().split('T')[0];
    
    return (
      <div className="space-y-6">
        {/* Distance badge */}
        {calculatedDistance && (
          <div className="flex justify-end">
            <Badge className="bg-emerald-100 text-emerald-800 text-sm px-3 py-1">
              Distance: {Math.round(calculatedDistance)} km
            </Badge>
          </div>
        )}

        {/* Header avec mode */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-600" />
              Planification des transferts
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Cliquez sur les dates du calendrier pour planifier vos rotations (dates passées autorisées)
            </p>
          </div>
        </div>

        {/* Mode édition: Simple date picker */}
        {mode === 'edit' ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4" />
                Date d'exécution *
              </Label>
              <Input type="date" value={formData.date} onChange={(e) => updateForm('date', e.target.value)} />
              {formData.date && formData.date < today && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  ⚠️ Date antérieure à aujourd'hui — saisie rétroactive
                </p>
              )}
            </div>
            <div>
              <Label className="mb-2 block">Shift</Label>
              <Select value={formData.shift} onValueChange={(value) => {
                updateForm('shift', value);
                if (value === 'day') {
                  updateForm('plannedStartTime', '06:00');
                  updateForm('plannedEndTime', '18:00');
                } else {
                  updateForm('plannedStartTime', '18:00');
                  updateForm('plannedEndTime', '06:00');
                }
              }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Jour (06:00 - 18:00)</SelectItem>
                  <SelectItem value="night">Nuit (18:00 - 06:00)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          /* Mode création/duplication: Calendrier interactif — sans minDate (dates passées autorisées) */
          <TransferDatePicker
            selectedDates={formData.plannedTransfers || []}
            onDatesChange={(dates) => {
              updateForm('plannedTransfers', dates);
              updateForm('numberOfTrucks', dates.length || 1);
              if (dates.length === 1) {
                updateForm('date', dates[0].date);
                updateForm('shift', dates[0].shift);
              }
            }}
            maxTransfers={formData.maxTransfersToCreate}
          />
        )}

        {/* Résumé rapide des transferts planifiés */}
        {mode !== 'edit' && transferCount > 0 && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-center gap-2 text-emerald-800">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">
                {transferCount} transfert{transferCount > 1 ? 's' : ''} planifié{transferCount > 1 ? 's' : ''}
              </span>
            </div>
            <p className="text-xs text-emerald-600 mt-1">
              Les ressources (chauffeurs/véhicules) pourront être assignées après création
            </p>
          </div>
        )}

        {/* Horaires personnalisés (optionnel) */}
        <div className="border-t pt-4">
          <Label className="text-sm font-medium text-gray-600 mb-3 block">
            Horaires personnalisés (optionnel)
          </Label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-2 mb-2 text-xs text-gray-500">
                <Clock className="w-3 h-3" />
                Heure de début
              </Label>
              <Input 
                type="time" 
                value={formData.plannedStartTime} 
                onChange={(e) => updateForm('plannedStartTime', e.target.value)} 
                className="text-sm"
              />
            </div>
            <div>
              <Label className="mb-2 block text-xs text-gray-500">Heure de fin</Label>
              <Input 
                type="time" 
                value={formData.plannedEndTime} 
                onChange={(e) => updateForm('plannedEndTime', e.target.value)} 
                className="text-sm"
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <Label className="mb-2 block">Notes / Instructions spéciales</Label>
          <Textarea
            value={formData.notes}
            onChange={(e) => updateForm('notes', e.target.value)}
            placeholder="Instructions particulières, remarques..."
            rows={3}
          />
        </div>
      </div>
    );
  };

  const renderStep5 = () => {
    const validAssignments = assignments.filter(a => (a.driver || a.manualDriverName) || (a.vehicle || a.manualVehiclePlate));
    
    return (
      <div className="space-y-4">
        <h3 className="font-semibold text-lg mb-4">Récapitulatif</h3>

        {/* Distance */}
        {calculatedDistance && (
          <div className="flex justify-end">
            <Badge className="bg-emerald-100 text-emerald-800 text-sm px-3 py-1">
              Distance totale: {Math.round(calculatedDistance)} km
            </Badge>
          </div>
        )}

        {/* Trajet */}
        <Card>
          <CardContent className="p-4">
            <h4 className="font-medium text-sm text-gray-500 mb-2">Trajet</h4>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">D</div>
                <p className="text-sm">{formData.departureAddress || '-'}</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">A</div>
                <p className="text-sm">{formData.arrivalAddress || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Détails */}
        <Card>
          <CardContent className="p-4">
            <h4 className="font-medium text-sm text-gray-500 mb-2">Détails de l'opération</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-400">Type de véhicule</p>
                <p className="font-medium">{getVehicleTypeLabel(formData.vehicleType)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Nature d'opération</p>
                <p className="font-medium">{getOperationTypeLabel(formData.operationType)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">{showContainerOptions ? 'Type de conteneur' : 'Type de marchandise'}</p>
                <p className="font-medium">{showContainerOptions ? getContainerTypeLabel(formData.containerType) : getMerchandiseTypeLabel(formData.merchandiseType)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Poids / Camions</p>
                <p className="font-medium">{formData.weight} T • {formData.numberOfTrucks} camion{formData.numberOfTrucks > 1 ? 's' : ''}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ressources */}
        {validAssignments.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h4 className="font-medium text-sm text-gray-500 mb-2">Ressources ({validAssignments.length}/{formData.numberOfTrucks})</h4>
              <div className="space-y-2">
                {validAssignments.map((a, idx) => (
                  <div key={idx} className="flex items-center gap-4 text-sm p-2 bg-gray-50 rounded">
                    <span className="font-medium">#{idx + 1}</span>
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4 text-gray-400" />
                      <span>{a.driver ? `${a.driver.firstName} ${a.driver.lastName || a.driver.nom}` : a.manualDriverName || 'Non assigné'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Truck className="w-4 h-4 text-gray-400" />
                      <span>{a.vehicle?.registration || a.vehicle?.plate || a.manualVehiclePlate || 'Non assigné'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Planning */}
        <Card>
          <CardContent className="p-4">
            <h4 className="font-medium text-sm text-gray-500 mb-2">Planification</h4>
            {/* Affichage mode calendrier (plusieurs transferts) */}
            {formData.plannedTransfers && formData.plannedTransfers.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs text-gray-400 mb-2">
                  {formData.plannedTransfers.length} transfert{formData.plannedTransfers.length > 1 ? 's' : ''} planifié{formData.plannedTransfers.length > 1 ? 's' : ''}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {formData.plannedTransfers
                    .sort((a, b) => {
                      if (a.date !== b.date) return a.date.localeCompare(b.date);
                      return a.shift === 'day' ? -1 : 1;
                    })
                    .map((t, idx) => (
                      <div key={idx} className={cn(
                        "flex items-center gap-2 p-2 rounded text-sm",
                        t.shift === 'day' ? "bg-amber-50" : "bg-indigo-50"
                      )}>
                        <span className="font-medium text-gray-600">#{idx + 1}</span>
                        <span>{new Date(t.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                        <Badge variant="outline" className={cn(
                          "text-xs",
                          t.shift === 'day' ? "bg-amber-100 text-amber-700" : "bg-indigo-100 text-indigo-700"
                        )}>
                          {t.shift === 'day' ? 'Jour' : 'Nuit'}
                        </Badge>
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              /* Affichage mode simple (1 transfert) */
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-400">Date</p>
                  <p className="text-sm font-medium">{formData.date}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Shift</p>
                  <p className="text-sm font-medium">{formData.shift === 'day' ? 'Jour' : 'Nuit'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Horaires</p>
                  <p className="text-sm font-medium">{formData.plannedStartTime} - {formData.plannedEndTime}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contract Info */}
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-400">Contrat</p>
          <p className="text-sm font-medium">{contractNumber} - {clientName}</p>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderStep0();
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      default: return null;
    }
  };

  const getTitle = () => {
    if (mode === 'edit') return `Modifier ${isMasculine ? 'le' : 'la'} ${terminology}`;
    if (mode === 'duplicate') return `Dupliquer ${isMasculine ? 'le' : 'la'} ${terminology}`;
    return `${isMasculine ? 'Nouveau' : 'Nouvelle'} ${terminology}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-full max-h-[92vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            {mode === 'duplicate' && <Copy className="w-5 h-5 text-blue-500" />}
            {getTitle()}
          </DialogTitle>
          <DialogDescription>
            {mode === 'edit' 
              ? `Modifier les informations ${isMasculine ? 'du' : 'de la'} ${terminology.toLowerCase()}`
              : mode === 'duplicate'
                ? `Créer ${isMasculine ? 'un nouveau' : 'une nouvelle'} ${terminology.toLowerCase()} basé${isMasculine ? '' : 'e'} sur ${isMasculine ? 'un existant' : 'une existante'}`
                : `Créer ${isMasculine ? 'un nouveau' : 'une nouvelle'} ${terminology.toLowerCase()} pour le contrat ${contractNumber}`
            }
          </DialogDescription>
        </DialogHeader>

        {/* Steps indicator */}
        <div className="flex items-center justify-between mb-4 px-2 flex-shrink-0">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            
            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                    isActive && "bg-red-500 text-white",
                    isCompleted && "bg-emerald-500 text-white",
                    !isActive && !isCompleted && "bg-gray-200 text-gray-500"
                  )}>
                    {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className={cn(
                    "text-xs mt-1",
                    isActive && "text-red-500 font-medium",
                    isCompleted && "text-emerald-500",
                    !isActive && !isCompleted && "text-gray-400"
                  )}>
                    {step.label}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={cn("flex-1 h-0.5 mx-2", index < currentStep ? "bg-emerald-500" : "bg-gray-200")} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto min-h-0 px-1">
          {renderStepContent()}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between pt-4 border-t flex-shrink-0">
          <Button
            variant="outline"
            onClick={currentStep === 0 ? onClose : goBack}
            data-testid="wizard-back-btn"
          >
            {currentStep === 0 ? 'Annuler' : (
              <>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Retour
              </>
            )}
          </Button>
          
          {currentStep < STEPS.length - 1 ? (
            <Button onClick={goNext} className="bg-red-500 hover:bg-red-600" data-testid="wizard-next-btn">
              Suivant
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading} className="bg-red-500 hover:bg-red-600" data-testid="wizard-submit-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {mode === 'edit' ? 'Enregistrer' : mode === 'duplicate' ? 'Dupliquer' : `Créer le ${terminology}`}
            </Button>
          )}
        </div>
      </DialogContent>

      {/* Save as Template Dialog (PM Spec 05/05/2026) */}
      <Dialog open={showSaveAsTemplate} onOpenChange={(open) => { if (!open) handleSkipSaveTemplate(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Sauvegarder comme template ?</DialogTitle>
            <DialogDescription>
              Réutilisez ce trajet en 1 clic pour vos prochains transferts récurrents.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="template-name" className="text-sm">Nom du template</Label>
            <Input
              id="template-name"
              autoFocus
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder='Ex: "Tournée Bingerville matin"'
              className="h-10"
              data-testid="save-template-name-input"
            />
            {pendingTemplateSnapshot && (
              <div className="text-xs text-slate-500 space-y-1 pt-1">
                <div>📍 {pendingTemplateSnapshot.departureAddress?.split(',')[0]} → {pendingTemplateSnapshot.arrivalAddress?.split(',')[0]}</div>
                {pendingTemplateSnapshot.tripType === 'round_trip' && <div>↻ Aller-retour</div>}
                {pendingTemplateSnapshot.vehicleType && <div>🚛 {pendingTemplateSnapshot.vehicleType}</div>}
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={handleSkipSaveTemplate} disabled={savingTemplate}
                    data-testid="skip-save-template-btn">
              Non, merci
            </Button>
            <Button onClick={handleSaveAsTemplate} disabled={savingTemplate || !newTemplateName.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="save-template-btn">
              {savingTemplate && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default CreateTransferWizard;
